import { useCallback, useEffect, useRef } from "react";
import { buildAllRoomRateplanCombos } from "../utils/roomRateCombos";

/**
 * Custom hook that owns field-change semantics for the CreatePromo form.
 * Provides a `handleFieldChange(path, value)` setter that mutates a nested
 * path on the promo object and seeds derived fields (allowedRateRoomCodes,
 * propertyCode) via effects.
 */
export const useCreatePromoFieldChange = ({
  promo,
  setPromo,
  rooms,
  ratePlans,
  // Property that `ratePlans`/`rooms` were fetched for — combos built from
  // them are tagged with it so a stale or foreign selection can be detected
  // downstream.
  ratePlansPropertyId = null,
  isEdit,
  isDuplicate,
  selectedProperty,
}) => {
  const handleFieldChange = useCallback(
    (fieldPath, value) => {
      setPromo((prevPromo) => {
        const pathArray = fieldPath.split(".");
        const updatedObject = { ...prevPromo };

        let current = updatedObject;
        for (let i = 0; i < pathArray.length - 1; i++) {
          const key = pathArray[i];
          current[key] = current[key] == null ? {} : { ...current[key] };
          current = current[key];
        }

        current[pathArray[pathArray.length - 1]] = value;
        return updatedObject;
      });
    },
    [setPromo]
  );

  // Page rooms/rate plans the default selection was last built from, so the
  // rebuild only fires when that data actually changes.
  const appliedPageDataRef = useRef({ rooms: null, ratePlans: null });

  const buildCombosForPageData = useCallback(
    (roomsData = [], ratePlansData = []) =>
      buildAllRoomRateplanCombos(roomsData, ratePlansData, ratePlansPropertyId),
    [ratePlansPropertyId]
  );

  useEffect(() => {
    if (isEdit || isDuplicate) return;

    // The form can target a property other than the one the page loaded (via
    // the Properties picker, which fetches its own data). A late page response
    // must not overwrite that selection with the page property's combos.
    const formProperties = promo?.selectedProperties || [];
    const targetsPageProperty =
      formProperties.length === 0 ||
      ratePlansPropertyId == null ||
      formProperties.some(
        (propertyId) => String(propertyId) === String(ratePlansPropertyId)
      );
    if (!targetsPageProperty) return;

    // Only rebuild when the page data itself is new. This effect also re-runs
    // when the Properties picker changes, and re-selecting the page property
    // would otherwise reset a partial selection back to every combo.
    const applied = appliedPageDataRef.current;
    if (applied.rooms === rooms && applied.ratePlans === ratePlans) return;
    appliedPageDataRef.current = { rooms, ratePlans };

    const selectedList = buildCombosForPageData(rooms, ratePlans);
    handleFieldChange("allowedRateRoomCodes", selectedList);
  }, [
    isEdit,
    isDuplicate,
    rooms,
    ratePlans,
    ratePlansPropertyId,
    promo?.selectedProperties,
    handleFieldChange,
    buildCombosForPageData,
  ]);

  useEffect(() => {
    handleFieldChange("propertyCode", selectedProperty.propertyCode);
  }, [selectedProperty, handleFieldChange]);

  return { handleFieldChange };
};
